import React from "react";
import {
  Box,
  Typography,
  Avatar,
  TextField,
  MenuItem,
  Tooltip,
  Chip,
  IconButton,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadingIcon from "@mui/icons-material/Downloading";
import { Download, Clear } from "@mui/icons-material";
import EvidenceCommentButton from "components/EvidenceCommentButton";
import PropTypes from "prop-types"; // Import PropTypes
import CommentButton from "components/CommentButton";
import { useAuth } from "context/authContext";

const EmployeeCompetencyCard = ({
  jobId,
  empId,
  empData,
  t,
  levelEditable = true,
  evidences = {},
  evidenceComments = {},
  downloadLoading = false,
  getBwStatus,
  handleLevelSelect,
  handleSaveCompToEmployeeEvidence,
  handleDownloadCompToEmployeeEvidence,
  handleSaveCompToEmployeeEvidenceComment,
  setPendingDelete,
  setConfOpen,
  setSelectedEmployeeForPoints,
  setPointsDialogOpen,
}) => {
  const { employee: me } = useAuth();

  return (
    <Box
      key={empId}
      sx={{
        mb: 3,
        border: "1px solid #e0e3e8",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      {/* Employee Header */}
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        px={2}
        py={2}
        borderBottom="1px solid #e5e7eb"
      >
        <Avatar
          src={empData.photo ? `data:image/jpeg;base64,${empData.photo}` : undefined}
          sx={{
            width: { xs: 45, sm: 55 },
            height: { xs: 45, sm: 55 },
          }}
        />
        <Typography variant="h5" sx={{ fontSize: { xs: 15, sm: 18 } }}>
          {empData.empName}
        </Typography>
      </Box>

      {/* Header (Hidden on Mobile) */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          px: 2,
          py: 1.5,
          backgroundColor: "#F6F5FA",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {t("user.trainings.competency")}
        </Typography>

        <Typography
          sx={{
            flex: "0 0 auto",
            textAlign: "right",
            fontWeight: 500,
            whiteSpace: "nowrap",
            mr: 4,
          }}
        >
          {t("user.trainings.currentLvl")}
        </Typography>
      </Box>

      {/* Rows */}
      {Object.entries(empData.competencies).map(([compId, comp]) => {
        const [, compName, , type] = comp.compMeta;
        const bwMapping = getBwStatus(jobId, comp.compMeta);
        const isBwActive = bwMapping?.bwActive || false;
        const compToJobId = bwMapping?.compToJobId;

        const compToEmpId = comp.compMeta[12];
        const savedEvidenceId = comp.compMeta[18];
        const empCurrentLvlValue = comp.compMeta[13];
        const savedEvidenceFileName = comp.compMeta[19];
        const savedEvidenceComment = comp.compMeta[20];
        const evidenceKey = compToEmpId;
        const adminComments = comp.compMeta[21];

        const fileName =
          evidences[evidenceKey] !== undefined ? evidences[evidenceKey] : savedEvidenceFileName;

        return (
          <Box
            key={compId}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              px: 2,
              py: 1.5,
              pl: 3,
              gap: 1.5,
              borderBottom: "1px solid #e0e3e8",
              "&:last-child": { borderBottom: "none" },
            }}
          >
            {/* Left */}
            <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
              <Box flex={1}>
                <Typography fontWeight={400} sx={{ fontSize: { xs: 14, sm: 18 } }}>
                  {compName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {type}
                </Typography>
              </Box>

              {isBwActive && (
                <Tooltip title="Manage Black/White Points">
                  <Chip
                    label="BW"
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                      if (levelEditable) {
                        setSelectedEmployeeForPoints({
                          empId,
                          empName: empData.empName,
                          compToJobId,
                          compName,
                        });
                        setPointsDialogOpen(true);
                      }
                    }}
                    sx={{ cursor: levelEditable ? "pointer" : "default" }}
                  />
                </Tooltip>
              )}
            </Box>

            {/* Right */}
            <Box
              sx={{
                width: { xs: "100%", sm: "auto" },
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "flex-start", sm: "flex-end" },
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: { xs: "block", sm: "none" },
                  mb: 0.5,
                  color: "text.secondary",
                }}
              >
                {t("user.trainings.currentLvl")}
              </Typography>

              <TextField
                size="small"
                select
                fullWidth
                value={comp.currentLevel?.name ?? ""}
                onChange={(e) => {
                  handleLevelSelect(empId, e.target.value, comp.compMeta, null);
                }}
                disabled={!levelEditable || isBwActive}
                helperText={isBwActive ? "Level is auto-calculated from BW points" : ""}
                sx={{
                  width: { xs: "100%", sm: 160 },
                  "& .MuiInputBase-root": {
                    height: 36,
                    borderRadius: "8px",
                    backgroundColor: isBwActive ? "#f0f7ff" : "#fff",
                  },
                }}
              >
                {comp.availableLevels.map((lvl) => (
                  <MenuItem key={lvl.id} value={lvl.name}>
                    {lvl.name} ({lvl.value})
                  </MenuItem>
                ))}
              </TextField>

              {/* Evidence Buttons Group */}
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  backgroundColor: fileName ? "#f8fafc" : "transparent",
                  borderRadius: "6px",
                  p: 0.25,
                }}
              >
                <input
                  type="file"
                  id={`file-input-${evidenceKey}`}
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    handleSaveCompToEmployeeEvidence(compToEmpId, file);
                  }}
                />

                <Tooltip title={fileName || t("user.evidenceProvider.uploadFile")} arrow>
                  <IconButton
                    component="span"
                    disabled={!levelEditable || !empCurrentLvlValue}
                    onClick={() => document.getElementById(`file-input-${evidenceKey}`).click()}
                    size="small"
                    color={fileName ? "success" : "default"}
                  >
                    <AttachFileIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip
                  title={
                    fileName
                      ? t("user.evidenceProvider.download")
                      : t("user.evidenceProvider.noFile")
                  }
                  arrow
                >
                  <IconButton
                    size="small"
                    disabled={!fileName}
                    color={fileName ? "primary" : "default"}
                    onClick={() => handleDownloadCompToEmployeeEvidence(compToEmpId, fileName)}
                  >
                    {downloadLoading ? (
                      <DownloadingIcon fontSize="small" />
                    ) : (
                      <Download fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>

                <EvidenceCommentButton
                  evidence={{
                    compToEmpId,
                    comment: evidenceComments?.[evidenceKey] ?? savedEvidenceComment ?? "",
                  }}
                  isEditable={levelEditable}
                  disabled={!fileName}
                  onSave={(newComment) =>
                    handleSaveCompToEmployeeEvidenceComment(compToEmpId, newComment)
                  }
                />
                <CommentButton
                  item={comp}
                  isPrimary={true}
                  isAdminScorecardView={true}
                  currentUser={me}
                  allComments={adminComments || []} // adminComments from destructured row
                  userAssessView={true} // MODIFIED: Forces the "Read-Only" mode you asked for
                  onSave={null} // MODIFIED: No save function needed for view mode
                  disabled={!adminComments || adminComments.length === 0} // MODIFIED: Disable if no comments to view
                />

                <Tooltip title={t("user.evidenceProvider.remove")} arrow>
                  <IconButton
                    size="small"
                    color={fileName ? "error" : "default"}
                    disabled={!levelEditable || !fileName}
                    onClick={() => {
                      setPendingDelete({ evidenceId: savedEvidenceId, compToEmpId });
                      setConfOpen(true);
                    }}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

EmployeeCompetencyCard.propTypes = {
  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  empId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  empData: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  levelEditable: PropTypes.bool,
  evidences: PropTypes.object,
  evidenceComments: PropTypes.object,
  downloadLoading: PropTypes.bool,
  getBwStatus: PropTypes.func.isRequired,
  handleLevelSelect: PropTypes.func.isRequired,
  handleSaveCompToEmployeeEvidence: PropTypes.func.isRequired,
  handleDownloadCompToEmployeeEvidence: PropTypes.func.isRequired,
  handleSaveCompToEmployeeEvidenceComment: PropTypes.func.isRequired,
  setPendingDelete: PropTypes.func.isRequired,
  setConfOpen: PropTypes.func.isRequired,
  setSelectedEmployeeForPoints: PropTypes.func.isRequired,
  setPointsDialogOpen: PropTypes.func.isRequired,
};

export default EmployeeCompetencyCard;
